"use client";

/**
 * GA4 + X Pixel + GTM の統合アナリティクスプロバイダ
 *
 * - GA4: gtag を読み込み、SPA遷移時に pageview を送信
 * - X Pixel: pixel スクリプトを読み込み、SPA遷移時に track('PageView') を発火
 * - GTM: コンテナを読み込み、SPA遷移時に spa_page_view を dataLayer へ push
 *
 * オプトアウト方式: consent !== "rejected"（未選択を含む）のときに動作する。
 * 電気通信事業法の外部送信規律が求めるのは通知・公表であり事前同意ではないため、
 * 初回訪問時のバナー + プライバシーポリシー第10条で通知し、拒否を選べるようにする。
 * 拒否時は GA4 のみ公式の無効化フラグで即時停止し（X Pixel / GTM に公式手段はない）、
 * 次回ページ読み込みから全スクリプトを読み込まない。
 * GA4 の measurement ID 未設定時も X Pixel / GTM は独立して動作する
 *
 * 責務分担: GA4 と X Pixel はここでコード直設置し、広告のコンバージョンタグは
 * GTM 管理画面側で運用する。GTM コンテナに GA4 設定タグを置くと二重計上になる。
 */

import { useEffect, useRef, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import Script from "next/script";
import Link from "next/link";
import { GA_MEASUREMENT_ID, pageview } from "@/lib/gtag";
import { trackXPixelPageView } from "@/lib/twq";
import { GTM_CONTAINER_ID, GTM_EVENTS, pushToDataLayer } from "@/lib/gtm";
import { readConsent, writeConsent, type ConsentState } from "@/lib/consent";

/**
 * GA4 公式の無効化フラグ。読み込み済みの gtag.js もこのフラグを見て送信を止めるため、
 * 拒否した瞬間のページで既にロードされた GA4 を即時停止できる。
 * https://developers.google.com/analytics/devguides/collection/gtagjs/user-opt-out
 */
function disableGa(): void {
  if (!GA_MEASUREMENT_ID) return;
  if (typeof window === "undefined") return;
  (window as unknown as Record<string, unknown>)[
    `ga-disable-${GA_MEASUREMENT_ID}`
  ] = true;
}

export default function AnalyticsProvider() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  // SSR とクライアントの初期値を null に揃え、ハイドレーションミスマッチを防ぐ
  const [consent, setConsent] = useState<ConsentState>(null);
  const [mounted, setMounted] = useState(false);
  // 前回のパスを記憶し、実際に変わったときだけ SPA 遷移イベントを送る。
  // 初回ページ分は各スクリプトのロード時自動計測（gtag config / twq config /
  // GTM All Pages トリガー）が担うため、初回は記録のみ行い送信しない。
  // consent の変化（バナー操作）ではパスが変わらないため、何も送信されない。
  // GA4 は pathname のみ、X Pixel / GTM は searchParams も見るため ref を分ける
  // 注意: 本コンポーネントはルートレイアウト直下に常駐する前提である。途中で再マウント
  // されると ref がリセットされて現在ページが「初回」扱いになり、pageview が1件欠落する
  // （next/script は同一 id のスクリプトを再実行しないため、ロード時自動計測では補われない）
  const prevGaPathRef = useRef<string | null>(null);
  const prevPageKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const stored = readConsent();
    // 拒否済みで再訪した場合もフラグを立てる（スクリプトはそもそも読み込まれないが、
    // 多層防御として GA4 側の無効化状態も一致させる）
    if (stored === "rejected") disableGa();
    setConsent(stored); // eslint-disable-line react-hooks/set-state-in-effect -- localStorage との同期に必要
    setMounted(true);
  }, []);

  // 拒否済みでない限り（未選択を含む）計測を有効にする
  const trackingEnabled = consent !== "rejected";

  // SPA ページ遷移時に GA4 pageview を送信する（パス変化時のみ）
  useEffect(() => {
    if (!trackingEnabled) return;
    if (prevGaPathRef.current === pathname) return;
    const isFirstPage = prevGaPathRef.current === null;
    prevGaPathRef.current = pathname;
    if (isFirstPage) return;
    pageview(pathname);
  }, [pathname, trackingEnabled]);

  // SPA ページ遷移時に X Pixel の Page View と GTM の遷移イベントを発火する（パス変化時のみ）
  // 2つを1つの useEffect にまとめている理由: 依存条件も初回スキップの意味論も完全に同一であり、
  // 分けるとスキップ漏れバグの発生面が増えるだけで得るものがない
  useEffect(() => {
    if (!trackingEnabled) return;
    const pageKey = `${pathname}?${searchParams.toString()}`;
    if (prevPageKeyRef.current === pageKey) return;
    const isFirstPage = prevPageKeyRef.current === null;
    prevPageKeyRef.current = pageKey;
    if (isFirstPage) return;
    trackXPixelPageView();
    pushToDataLayer(GTM_EVENTS.spaPageView, { page_path: pathname });
  }, [pathname, searchParams, trackingEnabled]);

  // マウント前は何も表示しない（SSR とクライアントの一致を保証する）
  // localStorage の読み取り前にスクリプトを描画すると、拒否済みユーザーにも
  // 一瞬読み込みが走るため、mounted になってから trackingEnabled で判定する
  if (!mounted) return null;

  const handleAccept = () => {
    writeConsent("accepted");
    setConsent("accepted");
  };

  const handleReject = () => {
    writeConsent("rejected");
    disableGa();
    setConsent("rejected");
  };

  return (
    <>
      {/* 拒否済みでない AND measurement ID 設定済みの場合のみ GA4 スクリプトを読み込む */}
      {GA_MEASUREMENT_ID && trackingEnabled && (
        <>
          <Script
            src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`}
            strategy="afterInteractive"
          />
          <Script id="gtag-init" strategy="afterInteractive">
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${GA_MEASUREMENT_ID}', { page_path: window.location.pathname });
            `}
          </Script>
        </>
      )}

      {/* 拒否済みでない場合のみ X Pixel ベーススクリプトを読み込む（GA4 設定とは独立）*/}
      {trackingEnabled && (
        <Script id="x-conversion-tracking" strategy="afterInteractive">
          {`
            !function(e,t,n,s,u,a){e.twq||(s=e.twq=function(){s.exe?s.exe.apply(s,arguments):s.queue.push(arguments);
            },s.version='1.1',s.queue=[],u=t.createElement(n),u.async=!0,u.src='https://static.ads-twitter.com/uwt.js',
            a=t.getElementsByTagName(n)[0],a.parentNode.insertBefore(u,a))}(window,document,'script');
            twq('config','rbxaf');
          `}
        </Script>
      )}

      {/* 拒否済みでない AND コンテナ ID 設定済みの場合のみ GTM を読み込む（GA4 設定とは独立）*/}
      {/* noscript の iframe は意図的に設置しない: JavaScript 無効環境では拒否の選択状態
          （localStorage）を読めず、拒否済みユーザーにも計測が走る。タグによる計測自体も
          JavaScript 前提であり、設置しても実質的な価値がない */}
      {GTM_CONTAINER_ID && trackingEnabled && (
        <Script id="gtm-container" strategy="afterInteractive">
          {`
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${GTM_CONTAINER_ID}');
          `}
        </Script>
      )}

      {/* 未選択時に通知バナーを表示する */}
      {consent === null && (
        <div
          role="dialog"
          aria-label="Cookieの使用について"
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-32px)] max-w-[480px] bg-surface rounded-2xl shadow-lg border border-border px-6 py-5 animate-slide-up"
        >
          <p className="text-text font-semibold text-sm mb-2">
            Cookieの使用について
          </p>
          <p className="text-subtext text-xs leading-relaxed mb-3">
            当サイトでは、利便性向上・利用状況の分析・広告効果測定のためにCookieを使用しています。拒否した場合、分析・広告効果測定のCookieは使用されません。
            <Link
              href="/privacy"
              className="text-accent hover:text-accent-hover underline ml-1"
            >
              プライバシーポリシー
            </Link>
          </p>
          <div className="flex flex-col sm:flex-row-reverse gap-2 sm:gap-3">
            <button
              onClick={handleAccept}
              className="bg-accent hover:bg-accent-hover text-on-accent text-sm font-medium px-5 py-2.5 rounded-lg transition-colors min-h-[44px]"
            >
              OK
            </button>
            <button
              onClick={handleReject}
              className="text-subtext hover:text-text text-sm font-medium px-5 py-2.5 rounded-lg transition-colors min-h-[44px]"
            >
              拒否する
            </button>
          </div>
        </div>
      )}
    </>
  );
}
